"use client";

import { useState, useEffect } from 'react';
import { Upload, Settings, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { AssetUploadContent } from './asset-upload-content';
import { ConfigCreationForm } from './config-creation-form';
import { TextAssetCreationForm } from './text-asset-creation-form';

type AssetType = {
    name: string;
    label: string;
    description: string;
};

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onComplete: () => void;
};

export function AssetCreationDialog({ isOpen, onClose, onComplete }: Props) {
    const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
    const [loadingTypes, setLoadingTypes] = useState(false);
    const [selectedTab, setSelectedTab] = useState<'upload' | 'config' | 'text'>('upload');

    // Fetch asset types on mount
    useEffect(() => {
        if (isOpen && assetTypes.length === 0) {
            fetchAssetTypes();
        }
    }, [isOpen, assetTypes.length]);

    const fetchAssetTypes = async () => {
        setLoadingTypes(true);
        try {
            const response = await fetch('/api/assets/types', {
                credentials: 'include'
            });
            if (!response.ok) {
                throw new Error('Failed to fetch asset types');
            }
            const data = await response.json();
            setAssetTypes(data.types || []);
        } catch (error) {
            console.error('Error fetching asset types:', error);
            toast.error('Failed to load asset types');
        } finally {
            setLoadingTypes(false);
        }
    };

    const handleComplete = () => {
        onComplete();
        onClose();
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="w-full max-w-2xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Create Asset</DialogTitle>
                </DialogHeader>

                <Tabs value={selectedTab} onValueChange={(value) => setSelectedTab(value as 'upload' | 'config' | 'text')} className="w-full flex flex-col flex-1 overflow-hidden">
                    <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
                        <TabsTrigger value="upload" className="flex items-center gap-2">
                            <Upload className="w-4 h-4" />
                            <span className="hidden sm:inline">Upload File</span>
                            <span className="sm:hidden">Upload</span>
                        </TabsTrigger>
                        <TabsTrigger value="config" className="flex items-center gap-2">
                            <Settings className="w-4 h-4" />
                            <span className="hidden sm:inline">Configuration</span>
                            <span className="sm:hidden">Config</span>
                        </TabsTrigger>
                        <TabsTrigger value="text" className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            <span className="hidden sm:inline">Text Asset</span>
                            <span className="sm:hidden">Text</span>
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="upload" className="mt-4 flex-1 overflow-y-auto">
                        <AssetUploadContent
                            onClose={handleClose}
                            onComplete={handleComplete}
                        />
                    </TabsContent>

                    <TabsContent value="config" className="mt-4 flex-1 overflow-y-auto">
                        {loadingTypes ? (
                            <div className="text-center py-8">Loading asset types...</div>
                        ) : (
                            <ConfigCreationForm
                                assetTypes={assetTypes}
                                onClose={handleClose}
                                onComplete={handleComplete}
                            />
                        )}
                    </TabsContent>

                    <TabsContent value="text" className="mt-4 flex-1 overflow-y-auto">
                        {loadingTypes ? (
                            <div className="text-center py-8">Loading asset types...</div>
                        ) : (
                            <TextAssetCreationForm
                                assetTypes={assetTypes}
                                onClose={handleClose}
                                onComplete={handleComplete}
                            />
                        )}
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}
